import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

// Panel bildirimleri (zil) — server: /api/notifications/storageRoles (hepsi POST).
// Bayide zil yok (#178). Argümandaki `scope` adresi seçer, gövdeye gitmez.
// Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md → "Bildirimler (1.2)"
// + manual-quota-qr.md §6 (#115)
const BASES = {
  storage: "/api/notifications/storageRoles",
};
const call = (path) => ({ scope, ...data } = {}) =>
  apiFunction(data, `${BASES[scope] || BASES.storage}/${path}`, "POST");
const PREF_FIELDS = ["tasksEmail"]; // manualQuotaEmail kaldırıldı (#129)

// Belirli bir endpoint'in tüm cache girdilerine recipe uygular (argümanlarda token olduğu için
// girdiler tek tek gezilir). Patch listesi döner (undo için).
export function patchNotificationCaches(dispatch, getState, endpointName, recipe) {
  const cache = getState()[notificationsApi.reducerPath]?.queries || {};
  const patches = [];
  Object.values(cache).forEach((q) => {
    if (q?.endpointName !== endpointName || !q.data) return;
    patches.push(
      dispatch(
        notificationsApi.util.updateQueryData(endpointName, q.originalArgs, (draft) => {
          if (draft?.data) recipe(draft.data);
        }),
      ),
    );
  });
  return patches;
}

const setUnreadCount = (dispatch, getState, unreadCount) => {
  if (typeof unreadCount !== "number") return;
  patchNotificationCaches(dispatch, getState, "getNotificationsUnreadCount", (d) => {
    d.unreadCount = unreadCount;
  });
  patchNotificationCaches(dispatch, getState, "getNotifications", (d) => {
    d.unreadCount = unreadCount;
  });
};

// Okundu işaretlemede listeyi ve sayacı iyimser günceller; yanıttaki unreadCount ile kesinleştirir.
const markMutation = (builder, path, { all = false } = {}) =>
  builder.mutation({
    query: call(path),
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      const now = new Date().toISOString();
      const ids = new Set(all ? [] : arg?.ids || []);
      const marked = new Set();
      const undo = patchNotificationCaches(dispatch, getState, "getNotifications", (d) => {
        (d.notifications || []).forEach((n) => {
          if (n.readAt || (!all && !ids.has(n._id))) return;
          n.readAt = now;
          marked.add(n._id);
        });
      });
      const countUndo = patchNotificationCaches(dispatch, getState, "getNotificationsUnreadCount", (d) => {
        d.unreadCount = all ? 0 : Math.max(0, (d.unreadCount || 0) - (undo.length ? marked.size : ids.size));
      });
      try {
        const { data } = await queryFulfilled;
        setUnreadCount(dispatch, getState, data?.data?.unreadCount);
      } catch {
        [...undo, ...countUndo].forEach((p) => p.undo());
      }
    },
  });

export const notificationsApi = createApi({
  reducerPath: "notifications",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  tagTypes: ["Notifications", "NotificationsCount", "NotificationPreferences"],
  keepUnusedDataFor: 60,
  endpoints(builder) {
    return {
      getNotifications: builder.query({
        query: call("list"),
        providesTags: ["Notifications"],
      }),
      getNotificationsUnreadCount: builder.query({
        query: call("unreadCount"),
        providesTags: ["NotificationsCount"],
      }),
      markNotificationsRead: markMutation(builder, "markRead"),
      markAllNotificationsRead: markMutation(builder, "markAllRead", { all: true }),
      getNotificationPreferences: builder.query({
        query: call("preferences/get"),
        providesTags: ["NotificationPreferences"],
      }),
      setNotificationPreferences: builder.mutation({
        query: call("preferences/set"),
        async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
          const fields = PREF_FIELDS.filter((f) => typeof arg?.[f] === "boolean");
          const undo = patchNotificationCaches(dispatch, getState, "getNotificationPreferences", (d) => {
            fields.forEach((f) => {
              d[f] = arg[f];
            });
          });
          try {
            const { data } = await queryFulfilled;
            const saved = data?.data || {};
            patchNotificationCaches(dispatch, getState, "getNotificationPreferences", (d) => {
              PREF_FIELDS.forEach((f) => {
                if (typeof saved[f] === "boolean") d[f] = saved[f];
              });
            });
          } catch {
            undo.forEach((p) => p.undo());
          }
        },
      }),
    };
  },
});

/**
 * Socket `notification:new` { notification, unreadCount } olayını RTK cache'lerine yazar:
 * açık listelerin başına ekler (aynı _id varsa öne taşır — 1 dk içi tekrar kuralı), sayacı ayarlar.
 * Hook'tan bağımsız test edilebilsin diye ayrı fonksiyon.
 */
export function applyNotificationNew(dispatch, getState, payload) {
  const notification = payload?.notification;
  if (notification?._id) {
    patchNotificationCaches(dispatch, getState, "getNotifications", (d) => {
      const list = Array.isArray(d.notifications) ? d.notifications : [];
      const existing = list.findIndex((n) => n._id === notification._id);
      if (existing >= 0) list.splice(existing, 1);
      else d.total = (d.total || 0) + 1;
      list.unshift(notification);
      d.notifications = list;
    });
  }
  if (typeof payload?.unreadCount === "number") {
    setUnreadCount(dispatch, getState, payload.unreadCount);
  }
}

export const {
  useGetNotificationsQuery,
  useGetNotificationsUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferencesMutation,
} = notificationsApi;
